import type { FormatDateOptions } from '@/types/DateTimeService'

export function formatDate (
	input: string,
	options: FormatDateOptions = {}
): string {
	const {
		showWeekday = false,
		showDate = true,
		showTime = false,
		showYear = false,
		autoFormat = false,
		locale = 'cs-CZ',
		timeZone = 'Europe/Prague'
	} = options

	// Parse input string into Date object
	const date = new Date(input)

	// Current date used for relative comparisons
	const now = new Date()

	/**
	 * AUTO FORMAT MODE
	 *
	 * Automatically decides which parts of the date should be displayed
	 * based on how far the date is from the current date.
	 */
	if (autoFormat) {
		const sameDay = isSameDay(date, now)
		const yesterday = isYesterday(date, now)
		const sameWeek = isSameWeek(date, now)
		const sameYear = isSameYear(date, now)

		// If the date is today → show "Dnes" + time
		if (sameDay) {
			const time = formatIntl(date, locale, timeZone, {
				hour: '2-digit',
				minute: '2-digit'
			})

			return `Dnes ${showTime ? time : ''}`
		}

		// If the date is yesterday → show "Včera" + time
		if (yesterday) {
			const time = formatIntl(date, locale, timeZone, {
				hour: '2-digit',
				minute: '2-digit'
			})

			return `Včera ${showTime ? time : ''}`
		}

		// If the date is within the current week → show weekday + time
		if (sameWeek) {
			return formatIntl(date, locale, timeZone, {
				weekday: 'long',
				hour: showTime ? '2-digit' : undefined,
				minute: showTime ? '2-digit' : undefined
			})
		}

		// If the date is within the current year → show day + month + time
		if (sameYear) {
			return formatIntl(date, locale, timeZone, {
				day: 'numeric',
				month: 'numeric',
				hour: showTime ? '2-digit' : undefined,
				minute: showTime ? '2-digit' : undefined
			})
		}

		// If the date is from a different year → include the year
		return formatIntl(date, locale, timeZone, {
			day: 'numeric',
			month: 'numeric',
			year: 'numeric',
			hour: showTime ? '2-digit' : undefined,
			minute: showTime ? '2-digit' : undefined
		})
	}

	/**
	 * MANUAL FORMAT MODE
	 *
	 * Uses explicit flags provided in options to determine
	 * which parts of the date should be displayed.
	 */
	const formatter = new Intl.DateTimeFormat(locale, {
		weekday: showWeekday ? 'long' : undefined,
		day: showDate ? 'numeric' : undefined,
		month: showDate ? 'numeric' : undefined,
		hour: showTime ? '2-digit' : undefined,
		minute: showTime ? '2-digit' : undefined,
		year: showYear ? 'numeric' : undefined,
		timeZone
	})

	return formatter.format(date)
}

/**
 * Wrapper around Intl.DateTimeFormat to ensure consistent
 * locale and timezone usage.
 */
function formatIntl (
	date: Date,
	locale: string,
	timeZone: string,
	options: Intl.DateTimeFormatOptions
) {
	return new Intl.DateTimeFormat(locale, {
		...options,
		timeZone
	}).format(date)
}

/**
 * Checks whether two dates occur on the same calendar day.
 */
export function isSameDay (a: Date, b: Date) {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	)
}

/**
 * Checks whether a date is exactly yesterday relative to another date.
 */
function isYesterday (date: Date, now: Date) {
	const yesterday = new Date(now)
	yesterday.setDate(now.getDate() - 1)

	return isSameDay(date, yesterday)
}

/**
 * Checks whether two dates belong to the same week.
 * Week start is Monday (ISO standard used in Europe).
 */
function isSameWeek (a: Date, b: Date) {
	const startOfWeek = (d: Date) => {
		const date = new Date(d)

		// Convert JS Sunday-based week to Monday-based
		const day = (date.getDay() + 6) % 7

		// Move date to Monday
		date.setDate(date.getDate() - day)

		// Normalize time
		date.setHours(0, 0, 0, 0)

		return date
	}

	return startOfWeek(a).getTime() === startOfWeek(b).getTime()
}

/**
 * Checks whether two dates belong to the same year.
 * @param a
 * @param b
 */
function isSameYear (a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear()
}
